import { Send, Bell, Download, Trash2, X, Link2, Pencil, CheckCircle2, XCircle, Eye } from 'lucide-react'
import { useLocale } from '../../contexts/LocaleContext'

interface Proposal {
  id: string
  proposal_number: string
  proposal_title: string
  client_name: string
  client_company?: string
  total_price: number
  currency: string
  status: string
  view_count: number
  created_at: string
  share_token?: string
  sent_at?: string
  viewed_at?: string
  signed_at?: string
  valid_until?: string
}

function formatRelativeTime(dateString: string, tx: (en: string, he: string) => string, locale: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return tx('Just now', 'עכשיו')
  if (diffMins < 60) return tx(`${diffMins} minutes ago`, `לפני ${diffMins} דקות`)
  if (diffHours < 24) return tx(`${diffHours} hours ago`, `לפני ${diffHours} שעות`)
  if (diffDays === 1) return tx('Yesterday', 'אתמול')
  if (diffDays < 7) return tx(`${diffDays} days ago`, `לפני ${diffDays} ימים`)
  return date.toLocaleDateString(locale === 'he' ? 'he-IL' : 'en-US')
}

function daysSince(d: string): number {
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
}

function getPrimaryAction(p: Proposal, tx: (en: string, he: string) => string): { label: string; intent: 'edit' | 'remind' | 'download' | 'view' } {
  if (p.status === 'signed' || p.status === 'accepted' || p.signed_at) return { label: tx('View Signed Proposal', 'צפה בהצעה חתומה'), intent: 'view' }
  if (p.status === 'draft') return { label: tx('Edit & Send', 'ערוך ושלח'), intent: 'edit' }
  if (p.status === 'sent' || p.status === 'viewed') return { label: tx('Send Reminder', 'שלח תזכורת'), intent: 'remind' }
  return { label: tx('Edit Proposal', 'ערוך הצעה'), intent: 'edit' }
}

function getStatusLabels(tx: (en: string, he: string) => string): Record<string, string> {
  return {
    draft: tx('Draft', 'טיוטה'),
    sent: tx('Sent', 'נשלח'),
    viewed: tx('Viewed', 'נצפה'),
    signed: tx('Signed', 'נחתם'),
    accepted: tx('Accepted', 'מאושר'),
    declined: tx('Declined', 'נדחה'),
    rejected: tx('Rejected', 'נדחה'),
    expired: tx('Expired', 'פג תוקף'),
  }
}

interface ProposalSidePanelProps {
  proposal: Proposal | null
  onClose: () => void
  onEdit: (id: string) => void
  onSendReminder: (id: string) => void
  onCopyLink: (shareToken: string) => void
  onDelete: (id: string) => void
  onAccept: (id: string) => void
  onDecline: (id: string) => void
  operatingId?: string | null
}

export default function ProposalSidePanel({
  proposal,
  onClose,
  onEdit,
  onSendReminder,
  onCopyLink,
  onDelete,
  onAccept,
  onDecline,
  operatingId,
}: ProposalSidePanelProps) {
  const { tx, locale } = useLocale()
  const statusLabels = getStatusLabels(tx)

  if (!proposal) {
    return (
      <aside className="bg-white rounded-2xl border border-slate-200 sticky top-24 overflow-hidden">
        <div className="p-5 text-center">
          <p className="text-slate-500 font-medium">{tx('Select a proposal from the list', 'בחר הצעה מהרשימה')}</p>
          <p className="text-sm text-slate-400 mt-1">
            {tx('This panel centralizes key actions for the selected proposal', 'פאנל זה מרכז פעולות מפתח להצעה הנבחרת')}
          </p>
        </div>
      </aside>
    )
  }

  const action = getPrimaryAction(proposal, tx)
  const days = daysSince(proposal.created_at)

  const handlePrimary = () => {
    if (action.intent === 'view' && proposal.share_token) {
      window.open(`${window.location.origin}/view/${proposal.share_token}`, '_blank')
    } else if (action.intent === 'edit') onEdit(proposal.id)
    else if (action.intent === 'remind') onSendReminder(proposal.id)
    else if (action.intent === 'download' && proposal.share_token) {
      window.open(`${window.location.origin}/view/${proposal.share_token}?print=1`, '_blank')
    }
  }

  const handleDownload = () => {
    if (proposal.share_token) {
      window.open(`${window.location.origin}/view/${proposal.share_token}?print=1`, '_blank')
    }
  }

  return (
    <aside className="bg-white rounded-2xl border border-slate-200 sticky top-24 overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-800 truncate">
            #{proposal.proposal_number} · {proposal.client_name}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5 truncate">
            {proposal.proposal_title}
            {proposal.client_company ? ` · ${proposal.client_company}` : ''}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 shrink-0"
          title={tx('Clear selection', 'נקה בחירה')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[10px] font-semibold text-slate-500 uppercase">{tx('Amount', 'סכום')}</div>
            <div className="text-sm font-bold text-slate-800 mt-0.5">
              {proposal.currency}{proposal.total_price?.toLocaleString() || 0}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[10px] font-semibold text-slate-500 uppercase">{tx('Views', 'צפיות')}</div>
            <div className="text-sm font-bold text-slate-800 mt-0.5">{proposal.view_count || 0}</div>
          </div>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[10px] font-semibold text-slate-500 uppercase">{tx('Days Ago', 'ימים')}</div>
            <div className="text-sm font-bold text-slate-800 mt-0.5">{days}</div>
          </div>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[10px] font-semibold text-slate-500 uppercase">{tx('Status', 'סטטוס')}</div>
            <div className="text-sm font-bold text-slate-800 mt-0.5">
              {statusLabels[proposal.status] || proposal.status}
            </div>
          </div>
        </div>

        {/* Primary Action */}
        <button
          onClick={handlePrimary}
          className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-white transition-all shadow-lg ${
            action.intent === 'view'
              ? 'bg-gradient-to-l from-emerald-600 to-emerald-600 hover:from-emerald-700 hover:to-emerald-700 shadow-emerald-500/20'
              : 'bg-gradient-to-l from-slate-700 to-slate-700 hover:from-slate-800 hover:to-slate-800 shadow-slate-500/20'
          }`}
        >
          {action.intent === 'view' && <Eye className="w-4 h-4" />}
          {action.intent === 'remind' && <Bell className="w-4 h-4" />}
          {action.intent === 'download' && <Download className="w-4 h-4" />}
          {action.intent === 'edit' && <Send className="w-4 h-4" />}
          <span>{action.label}</span>
        </button>

        {/* Accept / Decline — shown for active proposals awaiting response */}
        {(proposal.status === 'sent' || proposal.status === 'viewed') && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onAccept(proposal.id)}
              disabled={operatingId === proposal.id}
              className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4" />
              {tx('Accept', 'אשר')}
            </button>
            <button
              onClick={() => onDecline(proposal.id)}
              disabled={operatingId === proposal.id}
              className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="w-4 h-4" />
              {tx('Decline', 'דחה')}
            </button>
          </div>
        )}

        {/* Secondary Actions */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => proposal.share_token && onCopyLink(proposal.share_token)}
            disabled={!proposal.share_token}
            className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Link2 className="w-4 h-4" />
            {tx('Link', 'קישור')}
          </button>
          <button
            onClick={handleDownload}
            disabled={!proposal.share_token}
            className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            {tx('Download', 'הורדה')}
          </button>
          <button
            onClick={() => onEdit(proposal.id)}
            className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium col-span-2"
          >
            <Pencil className="w-4 h-4" />
            {tx('Edit', 'ערוך')}
          </button>
          <button
            onClick={() => onDelete(proposal.id)}
            className="col-span-2 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-rose-200 bg-rose-50/50 text-rose-600 hover:bg-rose-100 transition-colors text-sm font-medium"
          >
            <Trash2 className="w-4 h-4" />
            {tx('Delete', 'מחק')}
          </button>
        </div>

        {/* Timeline */}
        <div className="pt-4 border-t border-dashed border-slate-200 space-y-3">
          <div className="flex items-start gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-500 mt-1.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-slate-800">{tx('Created', 'נוצר')}</p>
              <p className="text-[11px] text-slate-500">{formatRelativeTime(proposal.created_at, tx, locale)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${proposal.sent_at ? 'bg-slate-500' : 'bg-slate-200'}`} />
            <div>
              <p className="text-xs font-semibold text-slate-800">{tx('Sent', 'נשלח')}</p>
              <p className="text-[11px] text-slate-500">{proposal.sent_at ? formatRelativeTime(proposal.sent_at, tx, locale) : '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${proposal.viewed_at ? 'bg-slate-500' : 'bg-slate-200'}`} />
            <div>
              <p className="text-xs font-semibold text-slate-800">{tx('Viewed', 'נצפה')}</p>
              <p className="text-[11px] text-slate-500">{proposal.viewed_at ? formatRelativeTime(proposal.viewed_at, tx, locale) : '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${proposal.signed_at ? 'bg-slate-500' : 'bg-slate-200'}`} />
            <div>
              <p className="text-xs font-semibold text-slate-800">{tx('Signed', 'נחתם')}</p>
              <p className="text-[11px] text-slate-500">{proposal.signed_at ? formatRelativeTime(proposal.signed_at, tx, locale) : '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
